import * as React from 'react';
import { useEffect, useReducer } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Card, Collapse, UncontrolledPopover } from 'reactstrap';
import qs from 'query-string';
import { ProductInventoryInfo, PartnerStockRow } from '../../models/Product';
import AddProductCardInfoModal from './AddProductCardInfoModal';
import ProductPriceInfo from './ProductPriceInfo';
import { ApplicationState } from '../../store';
import * as CartStore from '../../store/Cart';
import { nanoid } from 'nanoid';
import { formatMoney } from '../../components/shared/Utils';
import { buildMailTo } from './ProductMailHelper';
import * as Analytics from '../../AnalyticsHelper';
import * as AuthorizationStore from '../../store/Authorization';
import ProductInventoryService, { GetQuantityResult } from '../../services/ProductInventoryService';

type State = {
    expanded: boolean;
    view: 'stock' | 'details';
    productToBuy: PartnerStockRow;
    productQuantity: GetQuantityResult;
    addToCartModelOpened: boolean;
    unitsAddedToCart: number;
    itemsLikeThisInCart: number;
    isPricesOpen: boolean;
}

const initialState: State = {
    expanded: false,
    view: 'details',
    productToBuy: undefined,
    productQuantity: undefined,
    itemsLikeThisInCart: 0,
    unitsAddedToCart: 0,
    addToCartModelOpened: false,
    isPricesOpen: false,
}

function ProductInventoryCardInfo(props: {
    model: ProductInventoryInfo,
    cartState: CartStore.IState,
    cartActions: typeof CartStore.actionCreators,
    friendlySearchString: string,
    authorization: AuthorizationStore.IState;    
}) {

    let [state, setState] = useReducer(
        (prevState: State, newState: Partial<State>) => ({ ...prevState, ...newState }),
        initialState
    );

    const renderStock = () => {

        const ds1Length = Math.ceil(props.model.Stock.length / 2);
        const ds1 = props.model.Stock.slice(0, ds1Length);
        const ds2 = props.model.Stock.slice(ds1Length);

        return <div className="card-view view-stock">{renderStockTable(ds1)}{renderStockTable(ds2)}</div>;
    }

    const renderStockTable = (ds: PartnerStockRow[]) => (
        <table>
            <thead>
                <tr>
                    <th className="text-left">IMPACT PARTNER</th>
                    <th className="text-center">AVAILABLE</th>
                    <th className="text-center">ON ORDER</th>
                    <th className="text-center">CALL TO BUY</th>
                    <th className="text-right">COST</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {ds.map(x => <PartnerStockTableRowView key={x.PartnerName} productStockRow={x} productInventory={props.model} showCanadianPartners={props.authorization.currentUser.IsCanadian} expanded={state.expanded} onAdd={(productQuantity) => setState({
                    productToBuy: x,
                    productQuantity: productQuantity
                })} onCall={() => { }} />)}
            </tbody>
        </table>
    )

    const toggleView = (viewName: 'stock' | 'details') => {

        if (!state.expanded || state.view !== viewName) {
            Analytics.push({
                'event': viewName === 'stock' ? 'inet-price' : 'inet-details',
                'product-number': props.model.ProductNumber
            });
        }

        setState({
            expanded: !state.expanded || state.view !== viewName,
            view: viewName
        })
    }

    return <> <Card className={"product-iventory-card mb-3" + (state.expanded ? " card-expanded" : "")}>
        <div className={"card-top d-flex" + (props.model.Remanufactured ? " remanufactured" : "")}>
            <div className="card-top-section -section-codes">
                {props.model.ShortCode && <div className="product-code" title={props.model.ShortCode}>{props.model.ShortCode}</div>}
                <div className="product-number" title={props.model.ProductNumber}>
                    {props.model.ProductNumber}
                </div>
                {props.model.Remanufactured && <div className="label-remanufactured"><span className="label-remanufactured-inner">R</span></div>}
            </div>
            <div className="card-top-section section-description flex-grow-1">
                {props.model.Remanufactured && "[REMANUFACTURED] "} {props.model.Description}
            </div>
            {((props.model.PartnerPrices || []).length > 0) && <div className="card-top-section section-view-toggle">
                <a href="#" className="border border-secondary rounded-circle btn-view-toggle mx-auto"
                    onClick={(e) => {
                        e.preventDefault();
                        setState({ isPricesOpen: true });
                    }}><i className="fa fa-dollar-sign"></i></a>
                <div className="small-label text-grey uppercase mx-auto">
                    Sell Prices
                </div>
            </div>}
            {((props.model.DescriptionDetails || []).length > 0 || props.model.ListingPrice > 0) && <div className="card-top-section section-view-toggle">
                <a href="#" className="border border-secondary rounded-circle btn-view-toggle mx-auto"
                    onClick={(e) => {
                        e.preventDefault();
                        toggleView('details');
                    }}><i className="fa fa-ellipsis-h"></i></a>
                <div className="small-label text-grey uppercase mx-auto">
                    Details
                </div>
            </div>}
            <div className="card-top-section section-numbers">
                <div className="number">{props.model.PersonalStock}</div>
                <div className="small-label text-grey uppercase">
                    MY STOCK
                </div>
            </div>
            <div className="card-top-section section-numbers">
                <div className="number">{props.model.AllStock}</div>
                <div className="small-label text-grey uppercase">
                    ALL STOCK
                </div>
            </div>
            <div className="card-top-section section-numbers">
                <div className="number">{props.model.PartnersCount}</div>
                <div className="small-label text-grey uppercase">
                    PARTNERS
                </div>
            </div>
            <div className="card-top-section section-angle-toggle d-flex align-items-center">
                <a href="#" onClick={(e) => {
                    e.preventDefault();
                    toggleView('stock');
                }}><i className={(state.expanded && state.view === 'stock') ? "fa fa-angle-up" : "fa fa-angle-down"}></i></a>
            </div>
        </div>
        <Collapse isOpen={state.expanded}>
            {state.view === 'details' && <div className="card-view view-details">
                {props.model.ListingPrice > 0 && <div className="w-100">
                    <div className="attr d-flex px-2">
                        <span className="label-text flex-grow-1">List Price</span>
                        <span className="label-value">{new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 2,
                        }).format(props.model.ListingPrice)}</span>
                    </div>
                </div>}
                {props.model.ListingPrice === 0 && <div className="w-100">
                    <div className="attr d-flex px-2">
                        <span className="label-text flex-grow-1">List Price</span>
                        <span className="label-value">$--.--</span>
                    </div>
                </div>}
                {(props.model.DescriptionDetails || []).map(x => <div key={`${x.ProductId}${x.AttributeId}${x.CodeId}`} className="attr d-flex px-2">
                    <span className="label-text flex-grow-1">{x.AttributeName}</span>
                    <span className="label-value">{x.CodeName}</span>
                </div>)}
            </div>}
            {state.view === 'stock' && renderStock()}
        </Collapse>
    </Card>
        {<AddProductCardInfoModal
            inventory={props.model}
            partnerStock={state.productToBuy}
            productQuantity={state.productQuantity}
            cartState={props.cartState}
            onCancel={() => setState({
                productToBuy: undefined,
                unitsAddedToCart: 0,
            })}
            unitsAddedToCart={state.unitsAddedToCart}
            onAdd={(amount, approvedBy, cart) => {

                props.cartActions.add(cart.CartId, cart.CartName, {
                    CartId: cart.CartId,
                    ProductShortCode: props.model.ShortCode,
                    ProductNumber: props.model.ProductNumber,
                    Description: props.model.Description,
                    Remanufactured: props.model.Remanufactured,
                    PartnerId: state.productToBuy.PartnerId,
                    PartnerName: state.productToBuy.PartnerName,
                    PartnerPartNumber: state.productToBuy.ImpactPartnerPartNumber,
                    PricePerUnit: state.productToBuy.PricePerUnit,
                    ApprovedBy: approvedBy,
                    OrderCutoffTime: state.productToBuy.OrderCutoffTime,
                    OrderProcessingTime: state.productToBuy.OrderProcessing,
                    Amount: amount,
                    Available: true
                })

                setState({ productToBuy: undefined, unitsAddedToCart: 0 });
            }}
            userId={props.authorization.currentUser.UserId}
        />}
        {<ProductPriceInfo
            onCancel={() => setState({ isPricesOpen: false}) }
            inventory={props.model}
            isOpen={state.isPricesOpen} />}
    </>
}


export default connect(
    (state: ApplicationState) => ({
        cartState: state.cart,
        authorization: state.authorization
    }), // Selects which state properties are merged into the component's props
    (dispatch, getState) => ({
        cartActions: bindActionCreators(CartStore.actionCreators, dispatch)
    }))(ProductInventoryCardInfo);


type PartnerStockTableRowViewState = {
    expanded: boolean;
    productQuantity: GetQuantityResult;
    quantityLoaded: boolean;
    quantityErrorMessage: string;
}

function PartnerStockTableRowView(props: {
    productStockRow: PartnerStockRow;
    productInventory: ProductInventoryInfo;
    showCanadianPartners: boolean;
    expanded: boolean;
    onAdd: (productQuantity: GetQuantityResult) => void;
    onCall: () => void;
}) {

    let [state, setState] = useReducer(
        (prevState: PartnerStockTableRowViewState, newState: Partial<PartnerStockTableRowViewState>) => ({ ...prevState, ...newState }),
        {
            expanded: props.expanded,
            productQuantity: undefined,
            quantityLoaded: false,
            quantityErrorMessage: "",
        }
    );

    const getQuantity = (partnerStockRow: PartnerStockRow, productNumber: string) => {
        if (state.expanded && !state.quantityLoaded) {
            ProductInventoryService.getQuantity(partnerStockRow.PartnerId, productNumber)
                .then(
                    result => {
                        if (!result.hasErrors) {
                            if (!result.value.isReadOnly) {
                                setState({
                                    quantityLoaded: true,
                                    productQuantity: result.value,
                                    quantityErrorMessage: ""
                                });
                            }
                            else {
                                setState({
                                    quantityLoaded: true,
                                    productQuantity: {
                                        available: partnerStockRow.Stock,
                                        callToBuy: partnerStockRow.CallToBuy,
                                        onOrder: partnerStockRow.OnOrder,
                                        isReadOnly: true
                                    }, quantityErrorMessage: ""
                                })
                            }
                        }
                        else {
                            setState({ quantityLoaded: true, productQuantity: null, quantityErrorMessage: "Unable To Get Quantity" });
                        }
                    }
                );
        }
    }

    const [id1] = React.useState('id' + nanoid());
    const [id2] = React.useState('id' + nanoid());

    const [idNM] = React.useState('id' + nanoid());
    const [idC] = React.useState('id' + nanoid());
    const [idL] = React.useState('id' + nanoid());

    const isCanadianPartner = props.showCanadianPartners && props.productStockRow.IsCanadian;

    useEffect(() => {
        if (state.expanded)
            getQuantity(props.productStockRow, props.productInventory.ProductNumber);
    }, [state.expanded])

    const getAvailableQuantity = (productQuantity: GetQuantityResult) => {
        return productQuantity && productQuantity.available ? productQuantity.available : 0;
    }

    const getOnOrderQuantity = (productQuantity: GetQuantityResult) => {
        return productQuantity && productQuantity.onOrder ? productQuantity.onOrder : 0;
    }

    const getCallToBuyQuantity = (productQuantity: GetQuantityResult) => {
        return productQuantity && productQuantity.callToBuy ? productQuantity.callToBuy : 0;
    }

    const canAddToCart = (productQuantity: GetQuantityResult, pricePerUnit: number) => {
        if (productQuantity.isReadOnly) {
            return false;
        }

        const available = getAvailableQuantity(productQuantity);
        const callToBuy = getCallToBuyQuantity(productQuantity);
        const onOrder = getOnOrderQuantity(productQuantity);

        let qty = available + callToBuy;

        if (props.productStockRow.UseOnOrder) {
            qty += onOrder;
        }

        return pricePerUnit > 0 && qty > 0;
    }

    return <><tr><td className="text-nowrap"><span id={id1} className="label-text">{props.productStockRow.PartnerName}</span>
        {!props.productStockRow.ManagedInventory && <span id={idNM} className="label-partner-tag cursor-pointer label-nm" title="Non-Managed"><i>NM</i></span>}
        {props.productStockRow.Clearance && <span id={idC} className="label-partner-tag cursor-pointer label-c" title="Clearance"><i>C</i></span>}
        {props.productStockRow.LeadStocker && <span id={idL} className="label-partner-tag cursor-pointer label-l" title="Lead Stocker"><i>L</i></span>}
    </td>
        {!state.quantityLoaded &&
            <>
                <td colSpan={3}>
                    <div className="progress progress-qty" role="progressbar" aria-label="Get Quantity Progress" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100} >
                        <div className="progress-bar progress-bar-animated-gradient" style={{ width: "100%" }} ></div>
                    </div>
                </td>
                <td colSpan={2}>
                    <div className="progress progress-price" role="progressbar" aria-label="Get Quantity Progress" aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>
                        <div className="progress-bar progress-bar-static" style={{ width: "100%" }} ></div>
                    </div>
                </td>
            </>
        }
        {
            state.quantityLoaded && !state.quantityErrorMessage && 
            <>
                <td className="text-center">
                    <span id={id2} className="label-value">{getAvailableQuantity(state.productQuantity)}</span>
                </td>
                <td className="text-center">
                    <span className="label-value">{getOnOrderQuantity(state.productQuantity)}</span>
                </td>
                <td className="text-center">
                    <span className="label-value">{getCallToBuyQuantity(state.productQuantity)}</span>
                </td>
                <td className="text-right">{isCanadianPartner && <img className="cdn-flag mr-1" src="/images/CdnFlag.png" alt="Canadian Partner" />}{<span className="label-value text-nowrap">{props.productStockRow.PricePerUnit > 0 ? formatMoney(props.productStockRow.PricePerUnit) : "$--.--"}</span>}</td>
                <td className="text-right">
                    {
                        canAddToCart(state.productQuantity, props.productStockRow.PricePerUnit) &&
                        <button type="button" className="btn-action" onClick={() => props.onAdd(state.productQuantity)}>Add</button>}
                </td>
            </>
        }
        {
            state.quantityLoaded && state.quantityErrorMessage &&
            <>
                <td className="text-center" colSpan={3}>
                    <span className="label-value" style={{ fontSize: "12px", color: "red" }}>{state.quantityErrorMessage}</span>
                </td>
                <td className="text-right">{isCanadianPartner && <img className="cdn-flag mr-1" src="/images/CdnFlag.png" alt="Canadian Partner" />}{<span className="label-value text-nowrap">{props.productStockRow.PricePerUnit > 0 ? formatMoney(props.productStockRow.PricePerUnit) : "$--.--"}</span>}</td>
            </>
        }
    </tr>
        {
            state.quantityLoaded && !state.quantityErrorMessage &&
            <UncontrolledPopover placement="bottom" trigger="legacy focus" target={id1} className="popover-stock-info popover-bg-black">
                <div className="text-left p-2">
                    <div className="partner-name text-nowrap">{props.productStockRow.PriceAvailabilityContactName}</div>
                        <div><a href={buildMailTo(props.productStockRow, props.productInventory, state.productQuantity)} className="text-nowrap">{props.productStockRow.PriceAvailabilityContactEmail}</a></div>
                    <div className="text-nowrap">
                        <a href={'tel:' + props.productStockRow.PriceAvailabilityContactPhone} className="text-nowrap">{props.productStockRow.PriceAvailabilityContactPhone}</a>
                    </div>
                </div>
            </UncontrolledPopover>
        }

        {!props.productStockRow.ManagedInventory && <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idNM} className="popover-stock-info popover-bg-black">
            <div className="text-left p-2">
                <div className="partner-name text-nowrap">Non-Managed</div>
            </div>
        </UncontrolledPopover>}

        {props.productStockRow.Clearance && <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idC} className="popover-stock-info popover-bg-black">
            <div className="text-left p-2">
                <div className="partner-name text-nowrap">Clearance</div>
            </div>
        </UncontrolledPopover>}
        {props.productStockRow.LeadStocker && <UncontrolledPopover placement="bottom" trigger="legacy focus" target={idL} className="popover-stock-info popover-bg-black">
            <div className="text-left p-2">
                <div className="partner-name text-nowrap">Lead Stocker</div>
            </div>
        </UncontrolledPopover>}
    </>
}
